// c.f. ./card.tsx
import Link from 'next/link';
import Image from 'next/image';
import {
  Box,
  Heading,
  Icon,
  Stack,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { ArrowRightIcon } from '@heroicons/react/solid';
import { makeDocsLink } from '../utils';

type CardProps = {
  url: string;
  title: string;
  description: string;
  ctaText: string;
  icon?: React.FunctionComponent;
  logo?: string;
  logoHeight?: number;
  logoWidth?: number;
  size?: 'md' | 'lg';
};

export function ComponentPreviewCard({
  url,
  title,
  description,
  ctaText,
  icon,
  logo,
  logoHeight = 24,
  logoWidth = 24,
  size = 'md'
}: CardProps): JSX.Element {
  const headingSize = size === 'md' ? '2xs' : 'xs';
  const descriptionSize = size === 'md' ? 'xs-normal' : 'sm-normal';

  return (
    <Stack
      href={makeDocsLink(url)}
      as={Link}
      layerStyle="linkCard"
      bg="var(--cardBg)"
      role="group"
      justify="space-between"
      _hover={{
        boxShadow: useColorModeValue('dropShadow2', 'dropShadow1')
      }}
      m={3}
    >
      <Box>
        <Heading
          as="h4"
          size={headingSize}
          mb={2}
          color="var(--primaryTextColor)"
        >
          {title}
        </Heading>

        {logo && (
          <Box mb={4}>
            <Image
              src={logo}
              alt={`${title} logo`}
              height={logoHeight}
              width={logoWidth}
              style={{
                maxHeight: `${logoHeight}px`
              }}
            />
          </Box>
        )}

        {icon && (
          <Icon as={icon} color="var(--primaryBlue)" boxSize={6} mb={5} />
        )}

        <Text
          color="var(--secondaryTextColor)"
          textStyle={descriptionSize}
          maxW="60ch"
          mb={5}
        >
          {description}
        </Text>
      </Box>

      <Text
        color="var(--secondaryTextColor)"
        textStyle="xs-medium"
        _groupHover={{
          color: 'var(--secondaryBlue)'
        }}
      >
        {ctaText}
        <Icon
          as={ArrowRightIcon}
          ml={1}
          pos="relative"
          top="-1px"
          transition="margin-left .2s"
          _groupHover={{
            ml: 2
          }}
        />
      </Text>
    </Stack>
  );
}
